import getStandaloneHost from 'ff/common/components/getStandaloneHost';
import Cookies from 'ff/common/cookies';
import { RenderMenuParams } from 'ff/common/components/FFMenuV2/RenderMenuParams';

const FIELD_NAME = 'ffmenu-order';
export const GET_MENU_ORDER_LOCAL_STORAGE_KEY = (
    params: Pick<RenderMenuParams, 'currentUser'>
) => `${params.currentUser.accountUuid}_ffmenu-order`;

export interface MenuItemOrderPayload {
    order: string[];
    timestamp: number;
}

// FFMenuV2 can be embedded on hosts other than the one that owns this API (e.g. loaded
// standalone on dispatch.foreflight.com), so every call must tell the API which host it's
// really for.
function getApiHost() {
    return getStandaloneHost() || window.location.origin;
}

function isMenuItemOrderPayload(value: unknown): value is MenuItemOrderPayload {
    return (
        !!value &&
        typeof value === 'object' &&
        Array.isArray((value as MenuItemOrderPayload).order) &&
        typeof (value as MenuItemOrderPayload).timestamp === 'number'
    );
}

export function getLocalMenuItemOrder(
    params: Pick<RenderMenuParams, 'currentUser'>
): MenuItemOrderPayload | undefined {
    try {
        const raw = localStorage.getItem(
            GET_MENU_ORDER_LOCAL_STORAGE_KEY(params)
        );
        if (!raw) {
            return undefined;
        }
        const parsed = JSON.parse(raw);
        return isMenuItemOrderPayload(parsed) ? parsed : undefined;
    } catch (e) {
        return undefined;
    }
}

export function setLocalMenuItemOrder(
    payload: MenuItemOrderPayload,
    params: Pick<RenderMenuParams, 'currentUser'>
): void {
    try {
        localStorage.setItem(
            GET_MENU_ORDER_LOCAL_STORAGE_KEY(params),
            JSON.stringify(payload)
        );
    } catch (e) {
        console.info('Failed to save ffmenu item order to localStorage', e);
    }
}

function postOrderToServer(payload: MenuItemOrderPayload): Promise<void> {
    const url = `${getApiHost()}/account/api/settings/accountSettings/${FIELD_NAME}`;
    const xsrf = Cookies.getItem('_xsrf') ?? '';
    return fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRFToken': xsrf,
        },
        body: JSON.stringify({ value: payload }),
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(
                    `Failed to save ffmenu item order (${response.status})`
                );
            }
            return undefined;
        })
        .catch(e => {
            console.info('Failed to save ffmenu item order', e);
        });
}

// Used when the user actively reorders items: stamps a fresh timestamp, writes it to
// localStorage immediately, and pushes it up to the server.
export function saveMenuItemOrder(
    order: string[],
    params: Pick<RenderMenuParams, 'currentUser'>
): Promise<void> {
    const payload: MenuItemOrderPayload = { order, timestamp: Date.now() };
    setLocalMenuItemOrder(payload, params);
    return postOrderToServer(payload);
}

// Used to push a locally-newer payload (already timestamped) up to the server without
// generating a new timestamp, e.g. when reconciling on page load.
export function pushOrderToServer(
    payload: MenuItemOrderPayload
): Promise<void> {
    return postOrderToServer(payload);
}

export function fetchMenuItemOrder(): Promise<
    MenuItemOrderPayload | undefined
> {
    const url = `${getApiHost()}/account/api/settings/accountSettings/${FIELD_NAME}`;
    return fetch(url, { credentials: 'include' })
        .then(response => response.json())
        .then(resp => {
            const parsed = resp?.accountSettings?.value;
            return isMenuItemOrderPayload(parsed) ? parsed : undefined;
        })
        .catch(e => {
            console.info('Failed to fetch ffmenu item order', e);
            return undefined;
        });
}
